import { tidy } from '@aera/ui';
import { Reorder, useDragControls } from 'framer-motion';
import { ArrowUp, GripVertical, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fresh, type AnswerProps } from './common.js';

export const RANK_SLOTS = 3;

/**
 * I1: a true first choice, then an order for two more. Tap-to-number works everywhere (tap a card to give
 * it the next number, tap a numbered card to take it back); on desktop the numbered cards also drag, by
 * their grip.
 */
export function RankQuestion({ rq, value, onChange }: AnswerProps) {
  const { t } = useTranslation();
  const rank = value.rank ?? [];
  const labelOf = (id: string) => rq.options.find((o) => o.id === id)?.label ?? id;
  const set = (next: string[]) => onChange({ ...fresh(value), selected: [], rank: next });

  const tap = (id: string) => {
    if (rank.includes(id)) set(rank.filter((r) => r !== id));
    else if (rank.length < RANK_SLOTS) set([...rank, id]);
  };
  const moveUp = (id: string) => {
    const i = rank.indexOf(id);
    if (i <= 0) return;
    const next = [...rank];
    [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
    set(next);
  };

  return (
    <div className="space-y-4">
      <p className="text-[14px] text-ink-2">
        {t('question.rankHelp')} <span className="hidden sm:inline">{t('question.rankHelpDrag')}</span>
      </p>

      <Reorder.Group axis="y" values={rank} onReorder={set} className="space-y-2" aria-label={rq.prompt}>
        {rank.map((id, i) => (
          <RankedCard key={id} id={id} n={i + 1} label={labelOf(id)} onUp={i > 0 ? () => moveUp(id) : undefined} onRemove={() => tap(id)} />
        ))}
      </Reorder.Group>

      {rank.length < RANK_SLOTS && (
        <div role="group" aria-labelledby={`q-${rq.id}-label`} className="grid gap-2.5">
          {/* Left: the number the card would take. Right: its number key, renumbered as cards are taken. */}
          {rq.options
            .filter((o) => !rank.includes(o.id))
            .map((o, i) => (
              <button
                key={o.id}
                id={`opt-${rq.id}-${i + 1}`}
                type="button"
                aria-pressed={false}
                onClick={() => tap(o.id)}
                className="option-card flex w-full items-center gap-3 rounded-2xl border border-hair bg-white px-4 py-3 text-left text-[16px] leading-snug hover:border-[#cfcfd6]"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-hair text-[13px] text-ink-3" aria-hidden>
                  {rank.length + 1}
                </span>
                <span className="flex-1">{tidy(o.label)}</span>
                {rq.options.length <= 9 && (
                  <span data-hotkey className="hidden h-6 w-6 shrink-0 items-center justify-center rounded-md border border-hair text-[12px] text-ink-3 sm:flex" aria-hidden>
                    {i + 1}
                  </span>
                )}
              </button>
            ))}
        </div>
      )}

      {rank.length > 0 && (
        <button type="button" onClick={() => set([])} className="text-[14px] text-ink-2 underline-offset-4 hover:underline">
          {t('question.rankClear')}
        </button>
      )}
    </div>
  );
}

/**
 * A numbered card. It drags by the grip only: a drag listener on the whole card sets touch-action: pan-x,
 * so on a phone a swipe to scroll that starts on a card would reorder the list instead. Phones tap to
 * number; the grip shows from sm up.
 */
function RankedCard({ id, n, label, onUp, onRemove }: { id: string; n: number; label: string; onUp?: () => void; onRemove: () => void }) {
  const { t } = useTranslation();
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={id}
      dragListener={false}
      dragControls={controls}
      className="option-card flex items-center gap-3 rounded-2xl border border-indigo bg-[var(--selected-bg)] px-3 py-3"
      style={{ boxShadow: '0 0 0 1px var(--indigo)' }}
    >
      <span
        data-grip
        onPointerDown={(e) => controls.start(e)}
        className="hidden shrink-0 cursor-grab touch-none text-ink-3 active:cursor-grabbing sm:block"
        aria-hidden
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <span className="lp-pop flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo text-[13px] font-semibold text-white" aria-hidden>
        {n}
      </span>
      <span className="flex-1 text-[16px] leading-snug">
        <span className="sr-only">{t('question.rankSlot', { n })}</span>
        {tidy(label)}
      </span>
      {onUp && (
        <button type="button" onClick={onUp} className="rounded-full p-1.5 text-ink-2 hover:bg-white" aria-label={`↑ ${label}`}>
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
      <button type="button" onClick={onRemove} className="rounded-full p-1.5 text-ink-2 hover:bg-white" aria-label={`× ${label}`}>
        <X className="h-4 w-4" />
      </button>
    </Reorder.Item>
  );
}
